/**
 * Sistema de Rangos de Compra Progresivos
 * 
 * Controla el acceso a productos según el importe acumulado por rango en el carrito.
 * 
 * Rangos:
 * - Rango 1: Acceso libre
 * - Rango 2: Requiere 300€ en Rango 1
 * - Rango 3: Requiere 300€ en Rango 2 (y mantener 300€ en Rango 1)
 */

if ( typeof RangosSystem !== 'function' ) {

	class RangosSystem {
		
		constructor() {
			this.THRESHOLD_CENTS = 30000;
			this.cart = null;
			this.init();
		}

		init() {
			this.loadCart();
			
			document.addEventListener('cart-updated', () => {
				this.loadCart();
			});
			
			document.addEventListener('add-to-cart', async () => {
				await new Promise(resolve => setTimeout(resolve, 500));
				await this.loadCart();
			});
		}

		async loadCart() {
			try {
				const response = await fetch('/cart.js');
				this.cart = await response.json();
			} catch (error) {
				console.error('Error cargando carrito:', error);
			}
		}

		getProductRango(product) {
			if (!product || !product.metafields) return null;
			
			let rango = null;
			
			if (product.metafields.woo_attribute) {
				rango = product.metafields.woo_attribute.rango || product.metafields.woo_attribute.Rango;
			}
			
			if (!rango && product.metafields['woo_attribute.rango']) {
				rango = product.metafields['woo_attribute.rango'];
			}
			
			if (rango) {
				const parsed = parseInt(rango);
				if (!isNaN(parsed)) return parsed;
			}
			
			return null;
		}

		getItemRango(item) {
			if (!item) return null;
			
			if (item.properties && item.properties._rango) {
				const rango = parseInt(item.properties._rango);
				if (!isNaN(rango)) return rango;
			}
			
			if (item.product) {
				return this.getProductRango(item.product);
			}
			
			return null;
		}

		subtotalPorRango(rango) {
			if (!this.cart || !this.cart.items) return 0;
			return this.subtotalPorRangoEnCart(rango, this.cart);
		}

		puedeAñadir(product, cart = null) {
			const rango = this.getProductRango(product);
			
			if (!rango || rango === 1) {
				return { puede: true, mensaje: '' };
			}

			const cartToUse = cart || this.cart;
			
			if (!cartToUse || !cartToUse.items) {
				return { 
					puede: false, 
					mensaje: 'Cargando información del carrito...' 
				};
			}

			const subtotalR1 = this.subtotalPorRangoEnCart(1, cartToUse);
			const subtotalR2 = this.subtotalPorRangoEnCart(2, cartToUse);

			if (rango === 2) {
				if (subtotalR1 < this.THRESHOLD_CENTS) {
					const faltante = (this.THRESHOLD_CENTS - subtotalR1) / 100;
					return {
						puede: false,
						mensaje: `Añade productos del Rango 1 por valor de ${faltante.toFixed(2)} € para desbloquear este producto.`
					};
				}
				return { puede: true, mensaje: '' };
			}

			if (rango === 3) {
				if (subtotalR2 < this.THRESHOLD_CENTS) {
					const faltante = (this.THRESHOLD_CENTS - subtotalR2) / 100;
					return {
						puede: false,
						mensaje: `Añade ${faltante.toFixed(2)} € en productos del Rango 2 (y mantén 300 € en Rango 1) para desbloquear este producto.`
					};
				}
				return { puede: true, mensaje: '' };
			}

			return { puede: true, mensaje: '' };
		}

		subtotalPorRangoEnCart(rango, cart) {
			if (!cart || !cart.items) return 0;
			
			return cart.items.reduce((total, item) => {
				const itemRango = this.getItemRango(item);
				if (itemRango === rango) {
					return total + (item.final_line_price || item.line_price || 0);
				}
				return total;
			}, 0);
		}

		productosAfectadosPorEliminacion(itemToRemove, cart = null) {
			const cartToUse = cart || this.cart;
			if (!cartToUse || !cartToUse.items) return { afectados: [], rangosAfectados: [] };

			const rangoItem = this.getItemRango(itemToRemove);
			if (!rangoItem || rangoItem === 3) {
				return { afectados: [], rangosAfectados: [] };
			}

			const cartSimulado = {
				...cartToUse,
				items: cartToUse.items.filter(item => item.key !== itemToRemove.key)
			};

			const subtotalR1Simulado = this.subtotalPorRangoEnCart(1, cartSimulado);
			const subtotalR2Simulado = this.subtotalPorRangoEnCart(2, cartSimulado);

			const afectados = [];
			const rangosAfectados = [];

			if (rangoItem === 1 && subtotalR1Simulado < this.THRESHOLD_CENTS) {
				cartToUse.items.forEach(item => {
					const itemRango = this.getItemRango(item);
					if (itemRango === 2 || itemRango === 3) {
						afectados.push(item);
						if (!rangosAfectados.includes(itemRango)) {
							rangosAfectados.push(itemRango);
						}
					}
				});
			}

			if (rangoItem === 2 && subtotalR2Simulado < this.THRESHOLD_CENTS) {
				cartToUse.items.forEach(item => {
					const itemRango = this.getItemRango(item);
					if (itemRango === 3) {
						afectados.push(item);
						if (!rangosAfectados.includes(itemRango)) {
							rangosAfectados.push(itemRango);
						}
					}
				});
			}

			return { afectados, rangosAfectados };
		}

		productosAfectadosPorReduccion(itemToReduce, newQuantity, cart = null) {
			const cartToUse = cart || this.cart;
			if (!cartToUse || !cartToUse.items) return { afectados: [], rangosAfectados: [] };

			if (newQuantity >= (itemToReduce.quantity || 0)) {
				return { afectados: [], rangosAfectados: [] };
			}

			const rangoItem = this.getItemRango(itemToReduce);
			if (!rangoItem || rangoItem === 3) {
				return { afectados: [], rangosAfectados: [] };
			}

			const precioPorUnidad = itemToReduce.final_line_price / (itemToReduce.quantity || 1);
			const diferenciaCantidad = (itemToReduce.quantity || 0) - newQuantity;
			const diferenciaPrecio = precioPorUnidad * diferenciaCantidad;

			const cartSimulado = {
				...cartToUse,
				items: cartToUse.items.map(item => {
					if (item.key === itemToReduce.key) {
						return {
							...item,
							quantity: newQuantity,
							final_line_price: item.final_line_price - diferenciaPrecio,
							line_price: item.line_price - diferenciaPrecio
						};
					}
					return item;
				})
			};

			const subtotalR1Simulado = this.subtotalPorRangoEnCart(1, cartSimulado);
			const subtotalR2Simulado = this.subtotalPorRangoEnCart(2, cartSimulado);

			const afectados = [];
			const rangosAfectados = [];

			if (rangoItem === 1 && subtotalR1Simulado < this.THRESHOLD_CENTS) {
				cartToUse.items.forEach(item => {
					const itemRango = this.getItemRango(item);
					if (itemRango === 2 || itemRango === 3) {
						afectados.push(item);
						if (!rangosAfectados.includes(itemRango)) {
							rangosAfectados.push(itemRango);
						}
					}
				});
			}

			if (rangoItem === 2 && subtotalR2Simulado < this.THRESHOLD_CENTS) {
				cartToUse.items.forEach(item => {
					const itemRango = this.getItemRango(item);
					if (itemRango === 3) {
						afectados.push(item);
						if (!rangosAfectados.includes(itemRango)) {
							rangosAfectados.push(itemRango);
						}
					}
				});
			}

			return { afectados, rangosAfectados };
		}

		validarCarrito(cart = null) {
			const cartToUse = cart || this.cart;
			if (!cartToUse || !cartToUse.items) {
				return { valido: true, mensaje: '' };
			}

			for (const item of cartToUse.items) {
				const rango = this.getItemRango(item);
				if (!rango || rango === 1) continue;

				const cartSinItem = {
					...cartToUse,
					items: cartToUse.items.filter(i => i.key !== item.key)
				};

				const validacion = this.puedeAñadir(item.product, cartSinItem);
				if (!validacion.puede) {
					return {
						valido: false,
						mensaje: 'Tu carrito no cumple las condiciones de compra por rangos. Ajusta tus productos para continuar.'
					};
				}
			}

			return { valido: true, mensaje: '' };
		}

		obtenerProgreso() {
			if (!this.cart) {
				return {
					rango2: { desbloqueado: false, faltante: 300 },
					rango3: { desbloqueado: false, faltante: 300 }
				};
			}

			const subtotalR1 = this.subtotalPorRango(1);
			const subtotalR2 = this.subtotalPorRango(2);

			const rango2Desbloqueado = subtotalR1 >= this.THRESHOLD_CENTS;
			const rango2Faltante = rango2Desbloqueado ? 0 : (this.THRESHOLD_CENTS - subtotalR1) / 100;

			const rango3Desbloqueado = subtotalR2 >= this.THRESHOLD_CENTS;
			const rango3Faltante = rango3Desbloqueado ? 0 : (this.THRESHOLD_CENTS - subtotalR2) / 100;

			return {
				rango2: {
					desbloqueado: rango2Desbloqueado,
					faltante: rango2Faltante
				},
				rango3: {
					desbloqueado: rango3Desbloqueado,
					faltante: rango3Faltante
				}
			};
		}
	}

	// Crear instancia global
	window.RangosSystem = new RangosSystem();

}

